'use client';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { forwardRef, useRef } from 'react';
import { Box, createPolymorphicComponent } from '@s7/base';
import { useMergedRefs, useIsAutofill } from '@s7/hooks';
import { useProps } from '@s7/styles';
import { IDENT_INPUT, defaultProps } from './Input.contract.js';
import useStyles from './Input.styles.js';
import { InputRoot } from '../InputRoot/InputRoot.js';
import { InputPlaceholder } from '../InputPlaceholder/InputPlaceholder.js';
import { useInputRootContext } from '../InputRoot/InputRoot.context.js';
import { InputLabel } from '../InputRoot/InputLabel/InputLabel.js';
import { InputHint } from '../InputRoot/InputHint/InputHint.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var _Input = /*#__PURE__*/ forwardRef(function(_param, ref) {
    var _param___withStaticSelectors = _param.__withStaticSelectors, __withStaticSelectors = _param___withStaticSelectors === void 0 ? false : _param___withStaticSelectors, props = _object_without_properties_loose(_param, [
        "__withStaticSelectors"
    ]);
    var _useProps = useProps(IDENT_INPUT, defaultProps, props), _useProps_ = _useProps[0], className = _useProps_.className, style = _useProps_.style, sx = _useProps_.sx, id = _useProps_.id, required = _useProps_.required, disabled = _useProps_.disabled, state = _useProps_.state, states = _useProps_.states, color = _useProps_.color, radius = _useProps_.radius, width = _useProps_.width, withBorder = _useProps_.withBorder, Icon = _useProps_.icon, mountIcon = _useProps_.mountIcon, rightSide = _useProps_.rightSide, leftSide = _useProps_.leftSide, multiline = _useProps_.multiline, wrapperProps = _useProps_.wrapperProps, sxs = _useProps[1], others = _object_without_properties_loose(_useProps[0], [
        "className",
        "style",
        "sx",
        "id",
        "required",
        "disabled",
        "state",
        "states",
        "color",
        "radius",
        "width",
        "withBorder",
        "icon",
        "mountIcon",
        "rightSide",
        "leftSide",
        "multiline",
        "wrapperProps"
    ]);
    var context = useInputRootContext();
    var inferredProps = {
        state: context.state || state,
        states: context.states || states,
        disabled: context.disabled || disabled
    };
    var isError = inferredProps.state === "error";
    var icon = mountIcon && Icon ? /*#__PURE__*/ jsx(Icon, {
        state: inferredProps.state
    }) : null;
    var _useStyles = useStyles(_extends({
        radius: radius,
        multiline: multiline,
        color: color,
        withBorder: withBorder,
        width: width
    }, inferredProps), _extends({}, sxs, {
        __withStaticSelectors: __withStaticSelectors
    }), IDENT_INPUT), classes = _useStyles[0], cx = _useStyles[1];
    var innerRef = useRef(null);
    var mergedRefs = useMergedRefs(ref, innerRef);
    var isAutofill = useIsAutofill(innerRef.current).isAutofill;
    // Не прокидывается в Box
    /* eslint-disable @typescript-eslint/no-unused-vars */ var _ref = wrapperProps || {}; _ref.labelProps; _ref.hintProps; _ref.classNames; _ref.elementsOrder; _ref.inputContainer; _ref.styles; var onClick = _ref.onClick, restProps = _object_without_properties_loose(_ref, [
        "labelProps",
        "hintProps",
        "classNames",
        "elementsOrder",
        "inputContainer",
        "styles",
        "onClick"
    ]);
    /* eslint-enable @typescript-eslint/no-unused-vars */ return /*#__PURE__*/ jsxs(Box, _extends({}, restProps, {
        className: cx(classes.wrapper, className),
        "data-autofill": isAutofill,
        "data-disabled": disabled,
        style: style,
        sx: sx,
        onClick: function(event) {
            onClick == null ? void 0 : onClick(event);
            if (!event.defaultPrevented) {
                var _innerRef_current;
                (_innerRef_current = innerRef.current) == null ? void 0 : _innerRef_current.focus();
            }
        }
    }, inferredProps.state !== "default" && {
        "data-state": inferredProps.state
    }, {
        children: [
            leftSide && /*#__PURE__*/ jsx("div", {
                className: cx(classes.section, classes.leftSection),
                "data-left-section": true,
                children: /*#__PURE__*/ jsx("div", {
                    className: cx(classes.side, classes.leftSide),
                    children: leftSide
                })
            }),
            /*#__PURE__*/ jsx(Box, _extends({
                ref: mergedRefs,
                "aria-invalid": isError,
                as: "input",
                className: classes.input,
                "data-disabled": disabled || undefined,
                "data-left-section": !!leftSide,
                "data-right-section": !!(Icon || rightSide),
                disabled: disabled,
                id: id,
                required: required,
                type: "text"
            }, others)),
            (icon || rightSide) && /*#__PURE__*/ jsxs("div", {
                className: cx(classes.section, classes.rightSection),
                "data-right-section": true,
                children: [
                    icon && /*#__PURE__*/ jsx("div", {
                        className: classes.icon,
                        "data-right-side": !!rightSide,
                        children: icon
                    }),
                    rightSide && /*#__PURE__*/ jsx("div", {
                        className: cx(classes.side, classes.rightSide),
                        children: rightSide
                    })
                ]
            })
        ]
    }));
});
var Input = createPolymorphicComponent(_Input);
Input.Root = InputRoot;
Input.Label = InputLabel;
Input.Hint = InputHint;
Input.Placeholder = InputPlaceholder;
var Input$1 = Input;
Input.displayName = IDENT_INPUT;

export { Input, _Input, Input$1 as default };
